/**
 * Cosmetics v3 product information tabs: vanilla custom element, no dependencies.
 *
 * Each tab block renders its own button (role="tab") next to its panel (role="tabpanel"). This element
 * moves the buttons into the tablist, so the reading order follows the merchant's block order. A tab is
 * hidden when it is turned off or its panel has no content. The Theme Editor shows every tab.
 * Arrow keys move between visible tabs (wrapping), Home and End jump to the first and last. Selecting
 * a nested block in the Theme Editor opens its parent tab. Listeners are removed on disconnect.
 */
class Cv3ProductTabs extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;

    this.nav = this.querySelector('[data-cv3-pit-nav]');
    const buttons = [...this.querySelectorAll(':scope > [data-pit-tab]')];
    if (!this.nav || buttons.length === 0) {
      this.hidden = true;
      return;
    }

    buttons.forEach((button) => this.nav.append(button));
    this.tabs = buttons;

    const inDesignMode = Boolean(window.Shopify && window.Shopify.designMode);
    this.tabs.forEach((tab) => {
      const panel = this.panelFor(tab);
      const off = tab.dataset.pitOff === 'true' || panel?.dataset.pitOff === 'true';
      const empty = !panel || panel.dataset.pitEmpty === 'true';
      tab.hidden = !inDesignMode && (off || empty);
      if (panel && !inDesignMode && off) panel.hidden = true;
      tab.addEventListener('click', () => this.select(tab, { focus: false }), { signal });
      tab.addEventListener('keydown', (event) => this.onKeydown(event, tab), { signal });
    });

    const visible = this.visibleTabs();
    if (visible.length === 0) {
      this.hidden = true;
      return;
    }

    const current = visible.find((tab) => tab.getAttribute('aria-selected') === 'true') || visible[0];
    this.select(current, { focus: false });

    this.addEventListener('shopify:block:select', (event) => {
      const panel = event.target.closest?.('[role="tabpanel"]');
      if (!panel) return;
      const tab = this.tabs.find((item) => item.getAttribute('aria-controls') === panel.id);
      if (tab && !tab.hidden) this.select(tab, { focus: false });
    }, { signal });
  }

  disconnectedCallback() {
    this.controller?.abort();
  }

  panelFor(tab) {
    const id = tab.getAttribute('aria-controls');
    return id ? this.querySelector(`#${CSS.escape(id)}`) : null;
  }

  visibleTabs() {
    return this.tabs.filter((tab) => !tab.hidden);
  }

  // Shows the panel for the chosen tab and hides the rest. The tab stays in place, so the page does not jump.
  select(tab, { focus = true } = {}) {
    this.tabs.forEach((other) => {
      const selected = other === tab;
      other.setAttribute('aria-selected', selected ? 'true' : 'false');
      other.setAttribute('tabindex', selected ? '0' : '-1');
      const panel = this.panelFor(other);
      if (panel) panel.hidden = !selected;
    });
    tab.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    if (focus) tab.focus({ preventScroll: true });
  }

  onKeydown(event, tab) {
    const tabs = this.visibleTabs();
    const index = tabs.indexOf(tab);
    if (index === -1) return;
    let next = null;
    if (event.key === 'ArrowRight') next = tabs[(index + 1) % tabs.length];
    if (event.key === 'ArrowLeft') next = tabs[(index - 1 + tabs.length) % tabs.length];
    if (event.key === 'Home') next = tabs[0];
    if (event.key === 'End') next = tabs[tabs.length - 1];
    if (!next) return;
    event.preventDefault();
    this.select(next, { focus: true });
  }
}

if (!customElements.get('cv3-product-tabs')) {
  customElements.define('cv3-product-tabs', Cv3ProductTabs);
}
